/**
 * Catálogo de estudios locales (metadatos) sobre udvDicomCache.
 */
import {
  dicomCacheDb as db,
  DICOM_OFFLINE_CACHE_ENABLED,
  isDbAvailable,
  listAllCachedStudyUIDs,
  listCompleteSopsForStudy,
  listCachedSopsForStudy,
  getCachedInstance,
  getCombinedStudyCacheStats,
  limpiarCacheEstudio,
  vaciarCacheTotal,
  openDicomCacheDb,
  toArrayBuffer,
} from './dicomCache'
import {
  parseDicomStudyMeta,
  isPlaceholderPatientName,
} from '../utils/parseDicomStudyMeta'
import {
  sopFromManifestInstance,
  fallbackCacheKey,
} from '../utils/loadDicomInstanceWithCache'
import { resolveDiagnosticUrl } from '../utils/renderModeUrls'

async function ready() {
  if (!DICOM_OFFLINE_CACHE_ENABLED) return false
  // Verificar si el IDB ya se marcó como no disponible (timeout de bloqueo)
  if (!isDbAvailable()) return false
  try {
    if (!db.isOpen()) {
      // Timeout de 5 s: si el IDB está bloqueado por otra pestaña no colgamos el spinner
      await Promise.race([
        openDicomCacheDb(),
        new Promise((_, reject) =>
          setTimeout(
            () => reject(new Error('[studyCatalog] IDB open timeout (5 s)')),
            5000
          )
        ),
      ])
    }
    return true
  } catch (e) {
    console.warn('[studyCatalog] db open', e)
    return false
  }
}

export const STUDY_SOURCE = {
  GATEWAY: 'gateway',
  ORTHANC: 'orthanc',
  IMPORT: 'import',
  TICKET: 'ticket',
  UNKNOWN: 'unknown',
}

/**
 * @typedef {{
 *   studyInstanceUID: string,
 *   patientName?: string,
 *   patientId?: string,
 *   studyDate?: string,
 *   modality?: string,
 *   description?: string,
 *   instanceCount?: number,
 *   bytesTotal?: number,
 *   updatedAt?: number,
 *   source?: string,
 *   gatewayReceivedInstances?: number|null,
 *   ingestComplete?: boolean|null,
 *   edgeId?: string|null,
 *   edgeApiBase?: string|null,
 *   edgeWadoUrl?: string|null,
 *   clinicName?: string|null,
 *   cacheKind?: 'dicom'|'preview'|string,
 *   previewLayout?: Array<{
 *     sop: string,
 *     seriesNumber?: number,
 *     instanceNumber?: number,
 *     seriesUid?: string,
 *     modality?: string,
 *     seriesDescription?: string,
 *     previewUrl?: string|null,
 *   }>,
 * }} StudyCatalogRow
 */

/**
 * @param {StudyCatalogRow} meta
 * @returns {Promise<StudyCatalogRow|null>}
 */
export async function upsertStudyCatalog(meta) {
  if (!meta || !meta.studyInstanceUID || !DICOM_OFFLINE_CACHE_ENABLED) return null
  if (!(await ready())) return null
  const uid = String(meta.studyInstanceUID)
  const prev = await getStudyCatalog(uid)
  const row = {
    studyInstanceUID: uid,
    patientName:
      meta.patientName != null
        ? String(meta.patientName)
        : (prev && prev.patientName) || '',
    patientId:
      meta.patientId != null
        ? String(meta.patientId)
        : (prev && prev.patientId) || '',
    studyDate:
      meta.studyDate != null
        ? String(meta.studyDate)
        : (prev && prev.studyDate) || '',
    modality:
      meta.modality != null
        ? String(meta.modality)
        : (prev && prev.modality) || '',
    description:
      meta.description != null
        ? String(meta.description)
        : (prev && prev.description) || '',
    instanceCount:
      meta.instanceCount != null
        ? Number(meta.instanceCount) || 0
        : (prev && prev.instanceCount) || 0,
    bytesTotal:
      meta.bytesTotal != null
        ? Number(meta.bytesTotal) || 0
        : (prev && prev.bytesTotal) || 0,
    updatedAt: Date.now(),
    source: meta.source || (prev && prev.source) || STUDY_SOURCE.UNKNOWN,
    gatewayReceivedInstances:
      meta.gatewayReceivedInstances != null
        ? Number(meta.gatewayReceivedInstances)
        : prev
          ? prev.gatewayReceivedInstances
          : null,
    ingestComplete:
      meta.ingestComplete != null
        ? !!meta.ingestComplete
        : prev
          ? prev.ingestComplete
          : null,
    edgeId:
      meta.edgeId != null
        ? String(meta.edgeId)
        : prev
          ? prev.edgeId
          : null,
    edgeApiBase:
      meta.edgeApiBase != null
        ? String(meta.edgeApiBase)
        : prev
          ? prev.edgeApiBase
          : null,
    edgeWadoUrl:
      meta.edgeWadoUrl != null
        ? String(meta.edgeWadoUrl)
        : prev
          ? prev.edgeWadoUrl
          : null,
    clinicName:
      meta.clinicName != null
        ? String(meta.clinicName)
        : prev
          ? prev.clinicName
          : null,
    cacheKind:
      meta.cacheKind != null
        ? String(meta.cacheKind)
        : prev
          ? prev.cacheKind
          : '',
    previewLayout:
      meta.previewLayout != null
        ? meta.previewLayout
        : prev
          ? prev.previewLayout
          : undefined,
  }
  try {
    await db.study_catalog.put(row)
    return row
  } catch (e) {
    console.warn('[studyCatalog] upsert', e)
    return null
  }
}

/**
 * @param {string} studyInstanceUID
 * @returns {Promise<StudyCatalogRow|undefined>}
 */
export async function getStudyCatalog(studyInstanceUID) {
  if (!studyInstanceUID || !DICOM_OFFLINE_CACHE_ENABLED) return undefined
  if (!(await ready())) return undefined
  try {
    return await db.study_catalog.get(String(studyInstanceUID))
  } catch (e) {
    console.warn('[studyCatalog] get', e)
    return undefined
  }
}

/**
 * @returns {Promise<StudyCatalogRow[]>}
 */
export async function listStudyCatalog() {
  if (!DICOM_OFFLINE_CACHE_ENABLED) return []
  if (!(await ready())) return []
  try {
    const rows = await db.study_catalog.orderBy('updatedAt').reverse().toArray()
    return rows || []
  } catch (e) {
    console.warn('[studyCatalog] list', e)
    return []
  }
}

/**
 * @param {string} studyInstanceUID
 */
export async function removeStudyCatalog(studyInstanceUID) {
  if (!studyInstanceUID) return
  if (!(await ready())) return
  try {
    await db.study_catalog.delete(String(studyInstanceUID))
  } catch (e) {
    console.warn('[studyCatalog] remove', e)
  }
}

export async function clearStudyCatalog() {
  if (!(await ready())) return
  try {
    await db.study_catalog.clear()
  } catch (e) {
    console.warn('[studyCatalog] clear', e)
  }
}

/**
 * @param {StudyCatalogRow|null|undefined} row
 * @returns {boolean}
 */
export function catalogRowNeedsMetaEnrichment(row) {
  if (!row) return true
  return (
    isPlaceholderPatientName(row.patientName) ||
    !String(row.studyDate || '').trim() ||
    !String(row.modality || '').trim()
  )
}

/**
 * Lee PatientName/ID, StudyDate, Modality, Description del primer SOP usable en caché.
 * @param {string} studyInstanceUID
 * @returns {Promise<Partial<StudyCatalogRow>|null>}
 */
export async function readStudyMetaFromCache(studyInstanceUID) {
  const uid = String(studyInstanceUID || '')
  if (!uid || !DICOM_OFFLINE_CACHE_ENABLED) return null
  if (!(await ready())) return null

  let sops = Array.from(await listCompleteSopsForStudy(uid))
  if (sops.length === 0) {
    sops = await listCachedSopsForStudy(uid)
  }

  const limit = Math.min(sops.length, 2)
  for (let i = 0; i < limit; i++) {
    const cached = await getCachedInstance(sops[i])
    if (!cached || !cached.blob) continue
    const buf = await toArrayBuffer(cached.blob)
    if (!buf || buf.byteLength < 128) continue
    const meta = parseDicomStudyMeta(buf)
    if (
      meta.patientName ||
      meta.patientId ||
      meta.studyDate ||
      meta.modality ||
      meta.description
    ) {
      return {
        patientName: meta.patientName,
        patientId: meta.patientId,
        studyDate: meta.studyDate,
        modality: meta.modality,
        description: meta.description,
      }
    }
  }
  return null
}

/**
 * Actualiza stats + metadatos DICOM (solo campos no vacíos del parse).
 * @param {string} studyInstanceUID
 * @param {Partial<StudyCatalogRow>} [extra]
 * @returns {Promise<StudyCatalogRow|null>}
 */
export async function enrichStudyCatalogFromCache(studyInstanceUID, extra) {
  const uid = String(studyInstanceUID || '')
  if (!uid) return null
  const stats = await getCombinedStudyCacheStats(uid)
  const fromDicom = await readStudyMetaFromCache(uid)
  const prev = await getStudyCatalog(uid)
  /** @type {Partial<StudyCatalogRow>} */
  const patch = {
    studyInstanceUID: uid,
    instanceCount: stats.cachedComplete,
    bytesTotal: stats.bytes,
    cacheKind:
      stats.dicomComplete > 0
        ? 'dicom'
        : stats.previewComplete > 0
          ? 'preview'
          : prev && prev.cacheKind
            ? prev.cacheKind
            : '',
    ...(extra || {}),
  }
  if (fromDicom) {
    if (fromDicom.patientName) patch.patientName = fromDicom.patientName
    if (fromDicom.patientId) patch.patientId = fromDicom.patientId
    if (fromDicom.studyDate) patch.studyDate = fromDicom.studyDate
    if (fromDicom.modality) patch.modality = fromDicom.modality
    if (fromDicom.description) patch.description = fromDicom.description
  } else if (prev) {
    if (!patch.patientName && prev.patientName) patch.patientName = prev.patientName
    if (!patch.patientId && prev.patientId) patch.patientId = prev.patientId
    if (!patch.studyDate && prev.studyDate) patch.studyDate = prev.studyDate
    if (!patch.modality && prev.modality) patch.modality = prev.modality
    if (!patch.description && prev.description) patch.description = prev.description
  }
  // Estudio nuevo sin PatientName en el DICOM
  if (!patch.patientName) {
    if (!prev) patch.patientName = 'Desconocido'
  }
  return upsertStudyCatalog(patch)
}

/**
 * Si el catálogo está vacío pero hay blobs, crea filas stub (sin parse DICOM).
 * Evita congelar la UI al abrir Local Studies con caché grande / descarga activa.
 * @returns {Promise<StudyCatalogRow[]>}
 */
export async function backfillStudyCatalogStubsFromCache() {
  const existing = await listStudyCatalog()
  if (existing.length > 0) return existing

  const uids = await listAllCachedStudyUIDs()
  const now = Date.now()
  for (let i = 0; i < uids.length; i++) {
    const uid = uids[i]
    if (!uid) continue
    await upsertStudyCatalog({
      studyInstanceUID: String(uid),
      patientName: 'Desconocido',
      source: STUDY_SOURCE.UNKNOWN,
      updatedAt: now - i,
    })
  }
  return listStudyCatalog()
}

/**
 * Si el catálogo está vacío pero hay blobs, crea filas con meta desde DICOM.
 * @returns {Promise<StudyCatalogRow[]>}
 */
export async function backfillStudyCatalogFromCache() {
  const existing = await listStudyCatalog()
  if (existing.length > 0) return existing

  const uids = await listAllCachedStudyUIDs()
  const out = []
  for (let i = 0; i < uids.length; i++) {
    const row = await enrichStudyCatalogFromCache(uids[i], {
      source: STUDY_SOURCE.UNKNOWN,
    })
    if (row) out.push(row)
    // Ceder al event loop (no bloquear diálogo Local Studies)
    if (i > 0 && i % 2 === 0) {
      await new Promise((r) => setTimeout(r, 0))
    }
  }
  return out
}

/**
 * Lista catálogo; backfill si hace falta; enriquece filas incompletas desde blobs.
 * @param {{
 *   quick?: boolean,
 *   maxEnrich?: number,
 *   scanUnlisted?: boolean,
 * }} [opts]
 * @returns {Promise<StudyCatalogRow[]>}
 */
export async function listStudyCatalogOrBackfill(opts) {
  const o = opts || {}
  const quick = o.quick === true
  const maxEnrich =
    o.maxEnrich != null && Number.isFinite(Number(o.maxEnrich))
      ? Math.max(0, Math.floor(Number(o.maxEnrich)))
      : quick
        ? 0
        : 6
  const scanUnlisted = o.scanUnlisted !== false && !quick

  let rows = await listStudyCatalog()
  if (rows.length === 0) {
    rows = quick
      ? await backfillStudyCatalogStubsFromCache()
      : await backfillStudyCatalogFromCache()
  }

  if (quick || maxEnrich <= 0) {
    return rows
  }

  const out = []
  const known = new Set()
  let enriched = 0
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i]
    const uid = row && row.studyInstanceUID
    if (!uid) continue
    known.add(uid)
    if (catalogRowNeedsMetaEnrichment(row) && enriched < maxEnrich) {
      const next = await enrichStudyCatalogFromCache(uid, {
        source: row.source,
        gatewayReceivedInstances: row.gatewayReceivedInstances,
        ingestComplete: row.ingestComplete,
      })
      out.push(next || row)
      enriched += 1
      await new Promise((r) => setTimeout(r, 0))
    } else {
      out.push(row)
    }
  }

  // UIDs en caché aún no listados (omitir en quick / tras tope de enrich)
  if (scanUnlisted && enriched < maxEnrich) {
    const cachedUids = await listAllCachedStudyUIDs()
    for (let i = 0; i < cachedUids.length; i++) {
      if (enriched >= maxEnrich) break
      const uid = cachedUids[i]
      if (known.has(uid)) continue
      const next = await enrichStudyCatalogFromCache(uid, {
        source: STUDY_SOURCE.UNKNOWN,
      })
      if (next) {
        out.push(next)
        enriched += 1
        await new Promise((r) => setTimeout(r, 0))
      }
    }
  }

  out.sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0))
  return out
}

/**
 * Borra blobs + entrada de catálogo de un estudio.
 * @param {string} studyInstanceUID
 */
export async function deleteLocalStudy(studyInstanceUID) {
  const n = await limpiarCacheEstudio(studyInstanceUID)
  await removeStudyCatalog(studyInstanceUID)
  return n
}

/** Vacía caché DICOM/preview y catálogo. */
export async function clearAllLocalStudies() {
  await vaciarCacheTotal()
  await clearStudyCatalog()
  return true
}

/**
 * Refresca instanceCount/bytes desde dicom_cache para un UID.
 * @param {string} studyInstanceUID
 * @param {Partial<StudyCatalogRow>} [extra]
 */
export async function syncCatalogStatsFromCache(studyInstanceUID, extra) {
  if (!studyInstanceUID) return null
  const stats = await getCombinedStudyCacheStats(studyInstanceUID)
  return upsertStudyCatalog({
    studyInstanceUID,
    instanceCount: stats.cachedComplete,
    bytesTotal: stats.bytes,
    cacheKind:
      stats.dicomComplete > 0
        ? 'dicom'
        : stats.previewComplete > 0
          ? 'preview'
          : undefined,
    ...(extra || {}),
  })
}

/**
 * Construye layout compacto (serie/instancia) desde manifest para reapertura preview.
 * @param {object} manifest
 * @param {string} [assetBaseUrl]
 */
export function buildPreviewLayoutFromManifest(manifest, assetBaseUrl) {
  const m = manifest || {}
  const layout = []
  const seriesList = Array.isArray(m.series) ? m.series : []
  for (let si = 0; si < seriesList.length; si++) {
    const series = seriesList[si] || {}
    const instances = Array.isArray(series.instances) ? series.instances : []
    const seriesNumber = parseInt(series.seriesNumber, 10) || si + 1
    const seriesUid =
      series.seriesInstanceUID ||
      series.SeriesInstanceUID ||
      series.seriesUID ||
      ''
    const modality = series.modality || series.Modality || ''
    const seriesDescription = String(series.seriesDescription || '').trim()
    for (let ii = 0; ii < instances.length; ii++) {
      const inst = instances[ii]
      if (!inst) continue
      const diag = resolveDiagnosticUrl(inst, assetBaseUrl)
      const sop =
        sopFromManifestInstance(inst) || fallbackCacheKey(diag, inst) || ''
      if (!sop) continue
      layout.push({
        sop: String(sop),
        seriesNumber,
        instanceNumber: parseInt(inst.instanceNumber, 10) || ii + 1,
        seriesUid: String(seriesUid || ''),
        modality: String(inst.modality || inst.Modality || modality || ''),
        seriesDescription,
        previewUrl: inst.previewUrl || inst.webpUrl || inst.jpgUrl || null,
      })
    }
  }
  return layout
}

/**
 * Actualiza catálogo tras carga manifest (portal PREVIEW u on-demand clínico).
 * @param {object} manifest
 * @param {{
 *   assetBaseUrl?: string,
 *   expectedSopList?: string[],
 *   source?: string,
 * }} [opts]
 */
export async function syncStudyCatalogFromManifest(manifest, opts) {
  const m = manifest || {}
  const uid = String(m.studyInstanceUID || '')
  if (!uid) return null
  const o = opts || {}
  const previewLayout = buildPreviewLayoutFromManifest(m, o.assetBaseUrl)
  const expectedSops =
    Array.isArray(o.expectedSopList) && o.expectedSopList.length
      ? o.expectedSopList
      : previewLayout.map((row) => row.sop)
  const stats = await getCombinedStudyCacheStats(uid, expectedSops)
  const modalities = new Set()
  for (let i = 0; i < previewLayout.length; i++) {
    const mod = previewLayout[i] && previewLayout[i].modality
    if (mod) modalities.add(mod)
  }
  const modality =
    modalities.size === 1
      ? [...modalities][0]
      : modalities.size > 1
        ? 'MIX'
        : ''
  /** @type {Partial<StudyCatalogRow>} */
  const patch = {
    studyInstanceUID: uid,
    patientName: m.patientName || '',
    patientId: m.patientId || '',
    studyDate: m.studyDate || '',
    modality,
    description: m.studyDescription || '',
    instanceCount: stats.cachedComplete || expectedSops.length,
    bytesTotal: stats.bytes,
    source: o.source || STUDY_SOURCE.UNKNOWN,
    cacheKind:
      stats.dicomComplete > 0
        ? 'dicom'
        : stats.previewComplete > 0
          ? 'preview'
          : 'preview',
    previewLayout,
  }
  if (o.edgeId != null && String(o.edgeId).trim() !== '') {
    patch.edgeId = String(o.edgeId).trim()
  }
  if (o.clinicName != null && String(o.clinicName).trim() !== '') {
    patch.clinicName = String(o.clinicName).trim()
  }
  return upsertStudyCatalog(patch)
}
